import { applicationMaterials } from '../home_media_ui/applicationMaterials';
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Box, Button, Card, CardContent, Chip, Divider,
  Sheet, Slider, Stack, Typography, Select, Option,
  CircularProgress, Table, Input, Link, Modal, ModalDialog, ModalClose, IconButton, Tooltip
} from "@mui/joy";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import ArchitectureRoundedIcon from '@mui/icons-material/ArchitectureRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";

import { usePermissions } from "../usePermissions";
import { LayoutContext } from "../context";
import { getFirstName } from "../shared/nameUtils";
import OrderModal from "../components/modals/OrderModal";
import CalculatorModal from "../components/modals/CalculatorModal";
import { APIClientGetMyApplications, APIClientGetMyApplicationsPage, APIClientGetApplicationsStats, APIClientGetDrafts, APIClientGetDraft, APIClientDeleteDraft } from "../api";
import { LocalErrorBoundary } from "../GlobalErrorBoundary";
import OrderCard from "../components/ui/OrderCard";
import { groupOrdersByCity, getOrderCity, formatCityAddress } from "../shared/orderLocation";


// ─── City Stats Bar (серверные агрегаты) ──────────────────────────────────────
function CityStatsBar({ cities }) {
    const list = Array.isArray(cities) ? cities : [];
    // Не показываем если городов ≤ 1
    if (list.length <= 1) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box sx={{ fontSize: '1rem' }}>📍</Box>
                <Typography level="body-sm" sx={{ fontWeight: 700, color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    По городам
                </Typography>
            </Stack>
            <Box sx={{
                display: 'flex', gap: 1.5,
                overflowX: 'auto',
                pb: 1,  // чтобы скроллбар не обрезался
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(99,102,241,0.3)', borderRadius: 3 },
            }}>
                {list.map((cs, i) => {
                    const bs = cs.by_status || {};
                    return (
                    <motion.div
                        key={cs.city_id ?? cs.name}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        style={{ flexShrink: 0 }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: '16px',
                                minWidth: 200, p: 0,
                                cursor: 'default',
                                transition: 'transform .15s, box-shadow .15s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(99,102,241,0.15)',
                                },
                            }}
                        >
                            <CardContent sx={{ p: 1.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                                        {cs.name}
                                    </Typography>
                                    <Chip size="sm" variant="soft" color="primary">
                                        {cs.total}
                                    </Chip>
                                </Stack>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {(bs.completed ?? 0) > 0 && (
                                        <Chip size="sm" variant="soft" color="success" sx={{ fontSize: '0.7rem' }}>
                                            ✓ {bs.completed}
                                        </Chip>
                                    )}
                                    {(bs.on_drawing ?? 0) > 0 && (
                                        <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: '0.7rem' }}>
                                            ⏳ {bs.on_drawing}
                                        </Chip>
                                    )}
                                    {(bs.measuring_premises ?? 0) > 0 && (
                                        <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.7rem' }}>
                                            📅 {bs.measuring_premises}
                                        </Chip>
                                    )}
                                    {(bs.new ?? 0) > 0 && (
                                        <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: '0.7rem' }}>
                                            🆕 {bs.new}
                                        </Chip>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </motion.div>
                    );
                })}
            </Box>
        </Box>
    );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyOrders({ onNewOrder, canCreate = true, filtered = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
      <Sheet
        variant="soft" color="neutral"
        sx={{ borderRadius: "20px", p: 6, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
      >
        <Sheet
          variant="soft" color="primary"
          sx={{ width: 72, height: 72, borderRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.2rem" }}
        >
          📋
        </Sheet>
        <Box>
          <Typography level="title-md" fontWeight={700}>{filtered ? "Нет заявок по выбранному фильтру" : "Нет заявок"}</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, maxWidth: 280 }}>
            {filtered ? "Выберите вкладку «Все» или проверьте архив." : "Если заявку оформил менеджер, уточните у него привязку к вашему кабинету. Можно также создать новую заявку."}
          </Typography>
        </Box>
        {canCreate && (
          <Button startDecorator={<AddRoundedIcon />} onClick={onNewOrder} size="lg" sx={{ mt: 1 }}>
            Создать заявку
          </Button>
        )}
      </Sheet>
    </motion.div>
  );
}

// ─── Stats Bar (серверные агрегаты) ───────────────────────────────────────────
function StatsBar({ stats }) {
  const bs = stats?.by_status || {};
  const items = [
    { label: "Всего заявок",  value: stats?.total ?? 0,          color: "primary" },
    { label: "Завершено",     value: bs.completed ?? 0,          color: "success" },
    { label: "В работе",      value: bs.on_drawing ?? 0,         color: "primary" },
    { label: "Запланировано", value: bs.measuring_premises ?? 0, color: "warning" },
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 1.5, mb: 3 }}>
      {items.map((s, i) => (
        <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
          <Card variant="outlined" sx={{ borderRadius: "16px", p: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography level="h2" sx={{ fontWeight: 800, lineHeight: 1, letterSpacing: "-1px", color: `${s.color}.600` }}>
                {s.value}
              </Typography>
              <Typography level="body-xs" color="neutral" fontWeight={600} sx={{ mt: 0.5 }}>
                {s.label}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </Box>
  );
}

// ─── Filter Tabs ──────────────────────────────────────────────────────────────
const FILTERS = [
  { key: "all",                label: "Все" },
  { key: "measuring_premises", label: "Запл." },
  { key: "on_drawing",        label: "На черчении" },
  { key: "completed",          label: "Заверш." },
];

const VIEW_MODES = [
  { key: "cards", label: "Карточки", Icon: GridViewRoundedIcon },
  { key: "table", label: "Таблица",  Icon: TableRowsRoundedIcon },
];


// ─── HomePage (authenticated) ─────────────────────────────────────────────────
const TABLE_STATUS_COLOR = {
  completed: "success",
  measuring_premises: "primary",
  on_drawing: "warning",
  revision: "danger",
  new: "neutral",
  failed: "danger",
};

const ORDER_TABLE_COLUMNS = [
  { key: "num", label: "№", width: 90, sort: o => o.bitrix_deal || o.id },
  { key: "address", label: "Адрес", width: 280, sort: o => formatCityAddress(o).toLowerCase() },
  { key: "assigned", label: "Выезд", width: 155, sort: o => o.assigned_date || "" },
  { key: "deadline", label: "Дедлайн", width: 155, sort: o => o.deadline || "" },
  { key: "media", label: "Материалы", width: 100, sort: o => applicationMaterials(o) ? 1 : 0 },
  { key: "drawing", label: "Чертёж", width: 100, sort: o => applicationMaterials(o)?.ready ? 1 : 0 },
  { key: "actions", label: "Чат и приёмка", width: 370 },
  { key: "status", label: "Статус", width: 135, sort: o => o.status_display || o.status || "" },
];

const TABLE_SEARCH_FIELDS = [
  "bitrix_deal", "id", "address", "city_name", "custom_city_name",
  "status_display", "contact_name", "contact_phone", "manager_name",
  "surveyor_name", "object_type", "full_name", "phone",
];

function OrdersTable({ orders, onEdit, canEdit, onRefresh }) {
  const [selectedId, setSelectedId] = useState(null);
  const selectedOrder = orders.find(o => o.id === selectedId) || null;
  const [widths, setWidths] = useState(() => ORDER_TABLE_COLUMNS.map(c => c.width));
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d.toLocaleDateString("ru-RU") : "—";
  };

  const fmtDateTime = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (!Number.isFinite(d.getTime())) return "—";
    return d.toLocaleString("ru-RU", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // Дедлайн для клиента: внутренний дедлайн + 1 день (чисто визуально, как в карточке).
  const fmtDeadlinePlus1 = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (!Number.isFinite(d.getTime())) return "—";
    const plus1 = new Date(d);
    plus1.setHours(plus1.getHours() + 24);
    return plus1.toLocaleString("ru-RU", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  // Тянем границу заголовка → меняем ширину столбца
  const startResize = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[index];
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      setWidths(prev => {
        const next = [...prev];
        next[index] = Math.max(60, startW + dx);
        return next;
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const renderCell = (o, key) => {
    switch (key) {
      case "actions": return <OrderCard compact order={o} idx={0} onEdit={onEdit}
        canEdit={canEdit} isHidden={o.is_hidden} onRefresh={onRefresh} />;
      case "num":       return o.bitrix_deal ? `№${o.bitrix_deal}` : `#${o.id}`;
      case "address":   return formatCityAddress(o) || "—";
      case "status":    return (
        <Chip size="sm" variant="soft" color={TABLE_STATUS_COLOR[o.status] || "neutral"}>
          {o.status_display || o.status}
        </Chip>
      );
      case "contact":   return o.contact_name || "—";
      case "manager":   return o.manager_name || "—";
      case "surveyor":  return o.surveyor_name || "—";
      case "square":    return o.square ? `${o.square} м²` : "—";
      case "final":     return o.final_square ? `${o.final_square} м²` : "—";
      case "type":      return o.object_type || "—";
      case "assigned":  return o.assigned_date ? `${fmtDate(o.assigned_date)}${o.assigned_time ? " " + o.assigned_time : ""}` : "—";
      case "deadline":  return o.deadline ? fmtDeadlinePlus1(o.deadline) : "—";
      case "completed": return o.completed_at ? fmtDateTime(o.completed_at) : "—";
      case "created":   return fmtDate(o.date_create);
      case "media": {
        const materials = applicationMaterials(o);
        return materials ? <Link href={materials.url} color={materials.ready ? 'success' : 'warning'} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" onClick={e => e.stopPropagation()}>{materials.ready ? 'Чертежи готовы' : 'Материалы обмера'}</Link> : "—";
      }
      case "drawing": {
        const materials = applicationMaterials(o);
        return materials?.ready ? <Link href={materials.url} color="success" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" onClick={e => e.stopPropagation()}>Получить</Link> : "—";
      }
      default:          return "—";
    }
  };

  // Поиск по строке
  const q = query.trim().toLowerCase();
  const searched = q
    ? orders.filter(o => TABLE_SEARCH_FIELDS.some(f => String(o[f] ?? "").toLowerCase().includes(q)))
    : orders;

  // Сортировка по выбранному столбцу
  let rows = searched;
  if (sortKey) {
    const col = ORDER_TABLE_COLUMNS.find(c => c.key === sortKey);
    if (col && col.sort) {
      rows = [...searched].sort((a, b) => {
        const va = col.sort(a), vb = col.sort(b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
  }

  const totalWidth = widths.reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Input
          size="sm"
          placeholder="Поиск: адрес, №, менеджер, обмерщик, тип…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          startDecorator={<SearchRoundedIcon />}
          sx={{ maxWidth: 460, width: "100%", borderRadius: "10px" }}
        />
        <Typography level="body-xs" color="neutral">Найдено: {rows.length}</Typography>
      </Stack>

      <Sheet variant="outlined" sx={{
        width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box",
        borderRadius: "14px", overflowX: "auto", bgcolor: "background.surface",
        borderColor: "neutral.200",
        boxShadow: "0 4px 18px rgba(30, 25, 60, 0.04)",
      }}>
        <Table
          stickyHeader
          hoverRow
          size="sm"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: `${totalWidth}px`,
            fontSize: "13px",
            "--TableCell-headBackground": "var(--joy-palette-neutral-50)",
            "--TableCell-paddingX": "14px",
            "--TableCell-paddingY": "12px",
            "--TableRow-hoverBackground": "var(--joy-palette-primary-50)",
            "& thead th": {
              height: "48px", fontSize: "12px", fontWeight: 700,
              color: "text.secondary", borderBottom: "1px solid",
              borderColor: "neutral.200",
            },
            "& tbody td": { verticalAlign: "middle", lineHeight: 1.5 },
            "& tbody tr:nth-of-type(even)": { bgcolor: "neutral.50" },
            "& tbody tr:hover": { bgcolor: "primary.50" },
            "& tbody tr:last-child td": { borderBottom: 0 },
            "& tbody td:first-of-type": { fontWeight: 600, color: "primary.700" },
          }}
        >
          <colgroup>
            {widths.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}
          </colgroup>
          <thead>
            <tr>
              {ORDER_TABLE_COLUMNS.map((c, i) => (
                <th key={c.key} style={{ position: "relative", overflow: "hidden" }}>
                  <Box
                    onClick={c.sort ? () => toggleSort(c.key) : undefined}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      cursor: c.sort ? "pointer" : "default", userSelect: "none", pr: "10px",
                      overflow: "hidden", whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.label}</span>
                    {sortKey === c.key && (
                      <span style={{ fontSize: 10, color: "var(--joy-palette-primary-500)" }}>
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </Box>
                  {i < ORDER_TABLE_COLUMNS.length - 1 && (
                    <Box
                      onPointerDown={(e) => startResize(i, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: "absolute", top: 0, right: 0, height: "100%", width: "12px",
                        cursor: "col-resize", zIndex: 2, touchAction: "none",
                        "&::after": {
                          content: '""', position: "absolute", top: 12, bottom: 12, right: 1, width: "2px",
                          bgcolor: "neutral.300", borderRadius: 2,
                        },
                        "&:hover::after, &:active::after": { bgcolor: "primary.500", top: 4, bottom: 4 },
                      }}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr
                key={o.id}
                onClick={() => setSelectedId(o.id)}
                style={{ cursor: "pointer" }}
              >
                {ORDER_TABLE_COLUMNS.map((c) => (
                  <td key={c.key}
                    title={c.key === "address" ? formatCityAddress(o) : undefined}
                    style={{
                      overflow: c.key === "actions" ? "visible" : "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: ["actions", "address"].includes(c.key) ? "normal" : "nowrap",
                      overflowWrap: c.key === "address" ? "anywhere" : undefined,
                    }}>
                    {renderCell(o, c.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
      <Modal open={!!selectedOrder} onClose={() => setSelectedId(null)}>
        <ModalDialog sx={{ width: 760, maxWidth: 'calc(100vw - 24px)', maxHeight: '90dvh', overflow: 'auto', borderRadius: '22px', p: { xs: 2, sm: 3 }, boxShadow: '0 20px 70px rgba(24,24,48,0.2)' }}>
          <ModalClose />
          <Typography level="title-lg" fontWeight={800} sx={{ pr: 4, mb: 1 }}>Заявка №{selectedOrder?.bitrix_deal || selectedOrder?.id}</Typography>
          {selectedOrder && <OrderCard key={selectedOrder.id} detail order={selectedOrder} idx={0} canEdit={canEdit}
            isHidden={selectedOrder.is_hidden} onRefresh={onRefresh}
            onEdit={order => { setSelectedId(null); onEdit?.(order); }} />}
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default function HomePage() {
  const { account, showOrder, setShowOrder, setEditOrder } = useContext(LayoutContext);
  const [filter, setFilter] = useState("all");
  const [showHidden, setShowHidden] = useState(false);
  const [groupByCity, setGroupByCity] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("lk_orders_view_mode");
      return saved === "table" || saved === "cards" ? saved : "cards";
    } catch {
      return "cards";
    }
  }); // 'cards' | 'table'
  const [showCalc, setShowCalc] = useState(false);
  
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('create_application');
  const canEdit = hasPermission('edit_application');
  const canView = hasPermission('view_application');

 
  // ── Загрузка заявок с сервера ──────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const requestRevision = useRef(0);
  const sessionKey = `${account?.id || ""}:${localStorage.getItem("client_token") || ""}`;
  const currentSession = useRef(sessionKey);
  currentSession.current = sessionKey;
 
  const PAGE_SIZE = 20;
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  // Серверные агрегаты для плиток и «По городам» (не зависят от пагинации)
  const [stats, setStats] = useState(null);
  const loadingRef = useRef(false);        // защита от параллельных догрузок
  const sentinelRef = useRef(null);        // маркер конца списка для IntersectionObserver

  // Первая страница (и перезагрузка после создания/закрытия модалки)
  const loadOrders = useCallback(async () => {
    const revision = ++requestRevision.current;
    const session = currentSession.current;
    if (!account?.id || !canView) return;
    loadingRef.current = true;
    setOrdersLoading(true); setOrdersError("");
    try {
      const { results, total: t } = await APIClientGetMyApplicationsPage({ limit: PAGE_SIZE, offset: 0 });
      if (revision !== requestRevision.current || session !== currentSession.current) return;
      setOrders(results); setTotal(t);
    } catch (error) {
      if (revision === requestRevision.current && session === currentSession.current) {
        setOrdersError(error?.response?.data?.detail || error?.message || 'Не удалось загрузить заявки');
      }
    } finally {
      if (revision === requestRevision.current && session === currentSession.current) {
        setOrdersLoading(false); setLoadingMore(false); loadingRef.current = false;
      }
    }
  }, [account?.id, canView, sessionKey]);

  useEffect(() => {
    requestRevision.current += 1; loadingRef.current = false;
    setOrders([]); setTotal(0); setStats(null); setOrdersError("");
  }, [sessionKey]);

  useEffect(() => {
    try { localStorage.setItem("lk_orders_view_mode", viewMode); } catch {}
  }, [viewMode]);

  // Догрузка следующей страницы (по 20, пока не загрузим все)
  const loadMore = useCallback(async () => {
    if (loadingRef.current || orders.length >= total) return;
    const revision = requestRevision.current, session = currentSession.current;
    loadingRef.current = true; setLoadingMore(true); setOrdersError("");
    try {
      const { results } = await APIClientGetMyApplicationsPage({ limit: PAGE_SIZE, offset: orders.length });
      if (revision !== requestRevision.current || session !== currentSession.current) return;
      const seen = new Set(orders.map(o => o.id));
      const additions = results.filter(o => !seen.has(o.id));
      if (!additions.length) throw new Error('Список изменился. Нажмите «Обновить заявки».');
      setOrders(prev => [...prev, ...additions]);
    } catch (error) {
      if (revision === requestRevision.current && session === currentSession.current) {
        setOrdersError(error?.response?.data?.detail || error?.message || 'Не удалось загрузить следующую страницу');
      }
    } finally {
      if (revision === requestRevision.current && session === currentSession.current) {
        setLoadingMore(false); loadingRef.current = false;
      }
    }
  }, [orders, total]);

  // Агрегаты кабинета: считаются на сервере по всем заявкам клиента
  const loadStats = useCallback(async () => {
    const session = currentSession.current;
    if (!account?.id || !canView) return;
    const s = await APIClientGetApplicationsStats();
    if (session === currentSession.current && s && s.status !== 'error') setStats(s);
  }, [account?.id, canView, sessionKey]);

  useEffect(() => { loadOrders(); loadStats().catch(() => {}); }, [showOrder, loadOrders, loadStats]);

  // После привязки менеджером клиенту достаточно вернуться в кабинет.
  useEffect(() => {
    let lastRefresh = 0;
    const refresh = () => {
      if (document.visibilityState !== 'visible' || loadingRef.current || showOrder || !account?.id || !canView) return;
      const now = Date.now();
      if (now - lastRefresh < 3000) return;
      lastRefresh = now;
      loadOrders(); loadStats().catch(() => {});
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadOrders, loadStats, showOrder, account?.id, canView]);

  // Бесконечная прокрутка: как только маркер у низа списка попадает во вьюпорт —
  // подгружаем следующую страницу. Пересоздаём наблюдатель при росте списка,
  // чтобы при коротком (отфильтрованном) списке догрузка шла циклически до конца.
  const hasMore = orders.length < total;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || ordersError) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current(); },
      { rootMargin: '400px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, orders.length, ordersError]);

  // В табличном режиме догружаем все страницы, чтобы поиск/сортировка охватывали весь список
  useEffect(() => {
    if (viewMode === "table" && orders.length < total && !loadingMore && !loadingRef.current && !ordersError) {
      loadMore();
    }
  }, [viewMode, orders.length, total, loadingMore, loadMore, ordersError]);
 
  // ── Черновики (пункт 4) ──
  const [drafts, setDrafts] = useState([]);
  const loadDrafts = useCallback(async () => {
    const session = currentSession.current;
    if (!account?.id || !canCreate) {
      setDrafts([]);
      return;
    }
    const data = await APIClientGetDrafts();
    if (session !== currentSession.current) return;
    setDrafts(Array.isArray(data) ? data : []);
  }, [account?.id, canCreate, sessionKey]);
  useEffect(() => { setDrafts([]); }, [sessionKey]);
  useEffect(() => { loadDrafts(); }, [showOrder, loadDrafts]);

  const handleContinueDraft = async (id) => {
    const session = currentSession.current;
    const full = await APIClientGetDraft(id);
    if (session !== currentSession.current) return;
    if (full && full.status !== 'error') {
      setEditOrder(full);   // status === 'draft' → OrderModal откроется в режиме черновика
      setShowOrder(true);
    }
  };
  const handleDeleteDraft = async (id) => {
    await APIClientDeleteDraft(id);
    loadDrafts();
  };

  const visibleOrders = showHidden ? orders : orders.filter(o => !o.is_hidden);
  const hiddenCount = orders.filter(o => o.is_hidden).length;
  const filtered = filter === "all" ? visibleOrders : visibleOrders.filter(o => o.status === filter);
  const firstName = getFirstName(account);

  const handleEditOrder = (order) => {
    setEditOrder(order);
    setShowOrder(true);
  };

  if (!canView) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>
        <Sheet
          variant="soft" color="warning"
          sx={{ borderRadius: "20px", p: 6, textAlign: "center" }}
        >
          <Typography level="title-md" fontWeight={700}>Нет доступа к заявкам</Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
            У вас нет права на просмотр заявок. Обратитесь к администратору аккаунта.
          </Typography>
        </Sheet>
      </Box>
    );
  }
    
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>
 
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3.5 }}
        >
          <Box>
            <Typography level="h2" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>
              Здравствуйте{firstName ? `, ${firstName}` : ""}! 👋
            </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
              Ваш личный кабинет для отслеживания заявок
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                variant="outlined"
                color="primary"
                size="lg"
                startDecorator={<FunctionsRoundedIcon />}
                onClick={() => setShowCalc(true)}
                sx={{ fontWeight: 700, flexShrink: 0, borderRadius: "12px" }}
              >
                Калькулятор
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              {canCreate && (
                <Button
                  startDecorator={<AddRoundedIcon />}
                  size="lg"
                  onClick={() => { setEditOrder(null); setShowOrder(true); }}
                  sx={{
                    fontWeight: 700, flexShrink: 0, borderRadius: "12px",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                  }}
                >
                  Новая заявка
                </Button>
              )}
            </motion.div>
          </Stack>
        </Stack>
      </motion.div>
 
      {(stats?.total ?? 0) > 0 && <StatsBar stats={stats} />}
      {(stats?.total ?? 0) > 0 && <CityStatsBar cities={stats?.by_city} />}

      {canCreate && drafts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Box sx={{ fontSize: "1rem" }}>📝</Box>
            <Typography level="title-md" fontWeight={700}>Черновики</Typography>
            <Chip size="sm" variant="soft" color="warning">{drafts.length}</Chip>
          </Stack>
          <Stack spacing={1}>
            {drafts.map(d => (
              <Sheet key={d.id} variant="outlined" sx={{ borderRadius: "14px", p: 1.5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-sm" fontWeight={600} noWrap>
                      {d.object_type || "Заявка"}{d.city_name ? ` · ${d.city_name}` : ""}
                    </Typography>
                    <Typography level="body-xs" color="neutral" noWrap>
                      {d.address || "Адрес не указан"}{d.square ? ` · ${d.square} м²` : ""}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button size="sm" variant="soft" color="primary" onClick={() => handleContinueDraft(d.id)}>
                      Продолжить
                    </Button>
                    <Button size="sm" variant="plain" color="danger" onClick={() => handleDeleteDraft(d.id)}>
                      Удалить
                    </Button>
                  </Stack>
                </Stack>
              </Sheet>
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography level="title-md" fontWeight={700}>Мои заявки</Typography>
            {filtered.length > 0 && (
              <Chip size="sm" variant="soft" color="neutral">{filtered.length}</Chip>
            )}
            <Tooltip title={ordersLoading ? "Обновляем заявки" : "Обновить заявки"}>
              <span>
                <IconButton
                  aria-label="Обновить заявки"
                  aria-busy={ordersLoading}
                  variant="plain"
                  color="neutral"
                  disabled={ordersLoading}
                  onClick={() => {
                    if (loadingRef.current) return;
                    loadOrders();
                    loadStats().catch(() => {});
                    loadDrafts().catch(() => {});
                  }}
                  sx={{ width: 44, height: 44, borderRadius: "10px", '&:hover': { transform: 'none' } }}
                >
                  {ordersLoading ? <CircularProgress size="sm" /> : <SyncRoundedIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
 
          {orders.length > 0 && (
            <Sheet
              variant="soft" color="neutral"
              sx={{ borderRadius: "14px", p: 0.5, display: "flex", gap: 0.5 }}
            >
              {FILTERS.map(f => (
                <Button
                  key={f.key}
                  variant={filter === f.key ? "solid" : "plain"}
                  color={filter === f.key ? "primary" : "neutral"}
                  size="sm"
                  onClick={() => setFilter(f.key)}
                  sx={{
                    fontWeight: filter === f.key ? 700 : 500,
                    borderRadius: "10px", minHeight: 32, py: 0,
                    ...(filter === f.key && {
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    }),
                  }}
                >
                  {f.label}
                </Button>
              ))}
            </Sheet>
          )}

          {orders.length > 0 && (
            <Sheet
              role="group"
              aria-label="Вид списка заявок"
              variant="soft" color="neutral"
              sx={{ borderRadius: "14px", p: 0.5, display: "flex", gap: 0.5, flexShrink: 0 }}
            >
              {VIEW_MODES.map(vm => {
                const active = viewMode === vm.key;
                const Icon = vm.Icon;
                return (
                  <Button
                    key={vm.key}
                    aria-pressed={active}
                    variant={active ? "solid" : "plain"}
                    color={active ? "primary" : "neutral"}
                    size="sm"
                    onClick={() => setViewMode(vm.key)}
                    startDecorator={<Icon sx={{ fontSize: 18 }} />}
                    sx={{
                      fontWeight: active ? 700 : 500,
                      borderRadius: "10px", minHeight: 32, py: 0,
                      ...(active && {
                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      }),
                    }}
                  >
                    {vm.label}
                  </Button>
                );
              })}
            </Sheet>
          )}

          {viewMode !== "table" && orders.length > 1 && new Set(orders.map(getOrderCity).filter(Boolean)).size > 1 && (
            <Button
              variant={groupByCity ? "solid" : "soft"}
              color="primary"
              size="sm"
              onClick={() => setGroupByCity(prev => !prev)}
              startDecorator={
                  <span style={{ fontSize: '1rem' }}>{groupByCity ? '📍' : '📋'}</span>
              }
              sx={{ borderRadius: "10px", fontWeight: 500 }}
            >
              {groupByCity ? "По списку" : "По городам"}
            </Button>
          )}

          {hiddenCount > 0 && (
            <Button
              variant={showHidden ? "solid" : "soft"}
              color="neutral"
              size="sm"
              onClick={() => setShowHidden(prev => !prev)}
              sx={{ borderRadius: "10px", fontWeight: 500, gap: 0.5 }}
            >
              {showHidden ? "Скрыть архив" : `Архив (${hiddenCount})`}
            </Button>
          )}
        </Stack>
 
        {ordersError && (
          <Typography color="danger" role="alert" sx={{ mb: 2 }}>
            {ordersError}
          </Typography>
        )}

        {ordersLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm" color="neutral" sx={{ mt: 1.5 }}>Загружаем заявки...</Typography>
          </Stack>
        ) : filtered.length === 0 && !ordersError ? (
          <EmptyOrders onNewOrder={() => { setEditOrder(null); setShowOrder(true); }} canCreate={canCreate && filter === "all"} filtered={filter !== "all"} />
        ) : viewMode === "table" ? (
          <OrdersTable orders={filtered} onEdit={handleEditOrder} canEdit={canEdit} onRefresh={loadOrders} />
        ) : groupByCity ? (
          <Stack spacing={3}>
              {groupOrdersByCity(filtered).map(group => (
                  <Box key={group.city}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                          <Box sx={{
                              width: 4, height: 22, borderRadius: 2,
                              background: 'linear-gradient(180deg, #6366f1, #8b5cf6)',
                          }} />
                          <Typography level="title-md" sx={{ fontWeight: 700 }}>
                              {group.city}
                          </Typography>
                          <Chip size="sm" variant="soft" color="neutral">
                              {group.orders.length}
                          </Chip>
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gap: 1.5,
                          alignItems: "start",
                          gridTemplateColumns: "1fr",
                          "@media (min-width:1024px)": { gridTemplateColumns: "1fr 1fr" },
                        }}
                      >
                          {group.orders.map((o, i) => (
                              <LocalErrorBoundary key={o.id} name={`Заявка #${o.id}`}>
                                <OrderCard
                                  order={o}
                                  idx={i}
                                  onEdit={handleEditOrder}
                                  canEdit={canEdit}
                                  isHidden={o.is_hidden}
                                  onRefresh={loadOrders} />
                              </LocalErrorBoundary>
                          ))}
                      </Box>
                  </Box>
              ))}
          </Stack>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              alignItems: "start",
              gridTemplateColumns: "1fr",
              "@media (min-width:1024px)": { gridTemplateColumns: "1fr 1fr" },
            }}
          >
            {filtered.map((o, i) => (
              <LocalErrorBoundary key={o.id} name={`Заявка #${o.id}`}>
                <OrderCard order={o} idx={i} onEdit={handleEditOrder} canEdit={canEdit} isHidden={o.is_hidden} onRefresh={loadOrders} />
              </LocalErrorBoundary>
            ))}
          </Box>
        )}

        {/* Маркер бесконечной прокрутки + индикатор догрузки */}
        {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        {loadingMore && (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size="sm" />
          </Stack>
        )}
      </Box>

      <CalculatorModal open={showCalc} onClose={() => setShowCalc(false)} />
    </Box>
  );
}
